import { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, generateOrganizationSchema, generateWebPageSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

/* ─── Hero Background Image ─── */
const HERO_BG = 'https://readdy.ai/api/search-image?query=Abstract%20elegant%20dark%20navy%20blue%20background%20with%20subtle%20golden%20light%20particles%20and%20soft%20glowing%20bokeh%20orbs%20floating%20in%20deep%20space%20atmosphere%20luxurious%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20smooth%20gradients%20no%20text&width=1920&height=1080&seq=hero1&orientation=landscape';

/* ─── About Preview Image ─── */
const ABOUT_IMG = 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20Japanese%20corporate%20office%20interior%20with%20warm%20natural%20light%20streaming%20through%20large%20windows%20elegant%20wooden%20furniture%20clean%20lines%20sophisticated%20professional%20atmosphere%20Tokyo%20Marunouchi%20style%20high-end%20workspace%20no%20people&width=800&height=600&seq=about1&orientation=landscape';

/* ─── Service Images (shared with Business page) ─── */
const SERVICE_IMGS = [
  'https://readdy.ai/api/search-image?query=Elegant%20abstract%20artificial%20intelligence%20neural%20network%20visualization%20with%20golden%20nodes%20and%20connections%20in%20dark%20navy%20space%20soft%20glowing%20geometric%20patterns%20luxurious%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20no%20text&width=800&height=500&seq=biz2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Elegant%20abstract%20cyber%20security%20digital%20shield%20with%20golden%20glowing%20circuit%20board%20patterns%20lock%20symbol%20dark%20navy%20background%20sophisticated%20minimal%20corporate%20aesthetic%20golden%20light%20particles%20protection%20technology%20no%20text&width=800&height=500&seq=biz3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Elegant%20abstract%20voice%20wave%20visualization%20with%20golden%20particles%20floating%20in%20dark%20navy%20space%20soft%20glowing%20sound%20frequency%20curves%20luxurious%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20smooth%20gradients%20no%20text&width=800&height=500&seq=biz1&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Elegant%20abstract%20generative%20AI%20marketing%20digital%20data%20visualization%20golden%20flowing%20lines%20graphs%20analytics%20charts%20dark%20navy%20background%20sophisticated%20minimal%20corporate%20aesthetic%20glowing%20particles%20growth%20trend%20no%20text&width=800&height=500&seq=biz4&orientation=landscape',
];

/* ─── StatItem with CountUp ─── */
interface StatItemProps {
  number: string;
  label: string;
  visible: boolean;
  delay: number;
}

function StatItem({ number, label, visible, delay }: StatItemProps) {
  return (
    <div
      className="text-center"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      <div
        className="text-3xl md:text-4xl lg:text-5xl mb-3"
        style={{
          fontFamily: "'Playfair Display', 'Noto Serif JP', serif",
          fontWeight: 500,
          color: '#C9A227',
          letterSpacing: '0.02em',
        }}
      >
        {number}
      </div>
      <div
        className="text-xs tracking-widest-plus uppercase"
        style={{
          color: 'rgba(255,255,255,0.5)',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* ─── Service Card ─── */
interface ServiceCardProps {
  image: string;
  icon?: string;
  title: string;
  description: string;
  index: number;
  visible: boolean;
  learnMore?: string;
}

function ServiceCard({ image, icon, title, description, index, visible }: ServiceCardProps) {
  return (
    <div
      className="group cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.8s ease ${0.15 + index * 0.12}s, transform 0.8s ease ${0.15 + index * 0.12}s`,
      }}
    >
      <div
        className="relative overflow-hidden mb-6"
        style={{ aspectRatio: '3/2' }}
      >
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{
            background: 'linear-gradient(180deg, transparent 40%, rgba(10,25,47,0.6) 100%)',
          }}
        />
        {/* Gold corner accent */}
        <div
          className="absolute top-0 left-0 w-8 h-8 transition-all duration-500 group-hover:w-12 group-hover:h-12"
          style={{
            borderTop: '1px solid rgba(201,162,39,0.5)',
            borderLeft: '1px solid rgba(201,162,39,0.5)',
          }}
        />
        {/* Icon bottom-left */}
        {icon && (
          <div
            className="absolute bottom-3 left-3 w-9 h-9 flex items-center justify-center cursor-pointer"
            style={{
              background: 'rgba(10,25,47,0.6)',
              border: '1px solid rgba(201,162,39,0.4)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <i className={`${icon} text-sm`} style={{ color: '#C9A227' }} />
          </div>
        )}
      </div>
      <h3
        className="text-lg mb-3 transition-colors duration-300 group-hover:text-gold-500"
        style={{
          fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
          fontWeight: 500,
          color: '#1A1A1A',
        }}
      >
        {title}
      </h3>
      <p
        className="text-sm leading-relaxed"
        style={{
          color: '#333333',
          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
          fontWeight: 400,
        }}
      >
        {description}
      </p>

    </div>
  );
}

/* ─── News Preview Item ─── */
interface NewsItemProps {
  date: string;
  category: string;
  title: string;
  index: number;
  visible: boolean;
}

function NewsPreviewItem({ date, category, title, index, visible }: NewsItemProps) {
  return (
    <article
      className="group cursor-pointer py-5 border-b transition-colors duration-300"
      style={{
        borderColor: 'rgba(212, 207, 196, 0.4)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-20px)',
        transition: `opacity 0.6s ease ${0.1 + index * 0.08}s, transform 0.6s ease ${0.1 + index * 0.08}s, border-color 0.3s`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201, 162, 39, 0.3)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212, 207, 196, 0.4)';
      }}
    >
      <div className="flex items-start gap-5">
        <div className="shrink-0 pt-0.5">
          <time
            className="text-xs tracking-wider block"
            style={{
              color: '#A8A294',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {date}
          </time>
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="inline-block text-xs tracking-wider px-2.5 py-0.5 mb-2"
            style={{
              color: '#C9A227',
              border: '1px solid rgba(201, 162, 39, 0.3)',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
            }}
          >
            {category}
          </span>
          <h4
            className="text-base leading-relaxed transition-colors duration-300 group-hover:text-gold-600"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
              fontWeight: 500,
            }}
          >
            {title}
          </h4>
        </div>
        <i
          className="ri-arrow-right-line text-sm shrink-0 mt-1 transition-all duration-300 group-hover:translate-x-1"
          style={{ color: '#D4CFC4' }}
        />
      </div>
    </article>
  );
}

/* ─── Home Hero Particles ─── */
interface HeroParticle { id: number; x: number; y: number; size: number; opacity: number; duration: number; delay: number; driftX: number; driftY: number; }
interface HeroStream { id: number; x: number; height: number; duration: number; delay: number; opacity: number; }

/* ─── Main Page ─── */
export default function HomePage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };
  const [scrollY, setScrollY] = useState(0);
  const [heroMounted, setHeroMounted] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const heroParticles = useMemo<HeroParticle[]>(() =>
    Array.from({ length: 32 }, (_, i) => ({
      id: i,
      x: (i * 37.3 + 11) % 100,
      y: (i * 53.7 + 23) % 100,
      size: 1.5 + (i % 4) * 0.9,
      opacity: 0.12 + (i % 5) * 0.08,
      duration: 7 + (i % 7) * 2,
      delay: (i % 9) * 0.6,
      driftX: ((i % 5) - 2) * 14,
      driftY: -22 - (i % 6) * 9,
    })), []
  );

  const heroStreams = useMemo<HeroStream[]>(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      x: 3 + i * 7,
      height: 25 + (i % 5) * 18,
      duration: 3.5 + (i % 4) * 1.2,
      delay: i * 0.35,
      opacity: 0.05 + (i % 3) * 0.03,
    })), []
  );

  /* Scroll reveal hooks */
  const { ref: missionRef, visible: missionVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: servicesRef, visible: servicesVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: aboutRef, visible: aboutVisible } = useScrollReveal({ threshold: 0.15 });
  const { ref: techRef, visible: techVisible } = useScrollReveal({ threshold: 0.08 });
  const { ref: newsRef, visible: newsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: ctaRef, visible: ctaVisible } = useScrollReveal({ threshold: 0.2 });

  useSEO({
    title: t('seo.homeTitle'),
    description: t('seo.homeDescription'),
    keywords: t('seo.homeKeywords'),
    path: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateWebPageSchema(
          t('seo.homeTitle'),
          t('seo.homeDescription'),
          '/',
          getSchemaLang()
        ),
      ],
    },
  });

  useEffect(() => {
    setHeroMounted(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const heroH = heroRef.current?.offsetHeight ?? window.innerHeight;
  const heroProgress = Math.min(scrollY / heroH, 1);
  const heroOpacity = Math.max(1 - heroProgress * 1.8, 0);
  const heroParallax = scrollY * 0.35;

  const services = [
    {
      image: SERVICE_IMGS[0],
      icon: 'ri-robot-2-line',
      title: t('home.service1Title'),
      description: t('home.service1Desc'),
    },
    {
      image: SERVICE_IMGS[1],
      icon: 'ri-shield-keyhole-line',
      title: t('home.service3Title'),
      description: t('home.service3Desc'),
    },
    {
      image: SERVICE_IMGS[2],
      icon: 'ri-speaker-line',
      title: t('home.service2Title'),
      description: t('home.service2Desc'),
    },
  ];

  const techSkills = [
    {
      icon: 'ri-award-line',
      title: t('home.tech8Title'),
      desc: t('home.tech8Desc'),
    },
    {
      icon: 'ri-sparkling-2-line',
      title: t('home.tech1Title'),
      desc: t('home.tech1Desc'),
    },
    {
      icon: 'ri-cpu-line',
      title: t('home.tech4Title'),
      desc: t('home.tech4Desc'),
    },
    {
      icon: 'ri-brain-line',
      title: t('home.tech2Title'),
      desc: t('home.tech2Desc'),
    },
    {
      icon: 'ri-robot-line',
      title: t('home.tech3Title'),
      desc: t('home.tech3Desc'),
    },
    {
      icon: 'ri-wifi-line',
      title: t('home.tech5Title'),
      desc: t('home.tech5Desc'),
    },
    {
      icon: 'ri-fingerprint-line',
      title: t('home.tech6Title'),
      desc: t('home.tech6Desc'),
    },
    {
      icon: 'ri-database-2-line',
      title: t('home.tech7Title'),
      desc: t('home.tech7Desc'),
    },
  ];

  const newsItems = [
    { date: '2026.04.14', category: t('news.pressRelease'), title: t('home.newsItem1') || '医療従事者の負担軽減と入院患者の満足度向上を目指した音声アシスト病室の概念実証を実施' },
    { date: '2026.01.07', category: t('news.pressRelease'), title: t('home.newsItem2') || '元野村證券専務、元野村総合研究所副会長の百瀬裕規氏がTradFit社のエグゼクティブ‧アドバイザーに就任' },
    { date: '2025.12.02', category: t('news.pressRelease'), title: t('home.newsItem3') || 'トラベルボイスに掲載されました。TradFit社、NECネッツエスアイと販売店契約、AI音声サービス「ホスピタリア」のサポート体制を強化' },
    { date: '2025.11.21', category: t('news.pressRelease'), title: t('home.newsItem4') || 'Amazon Alexa Smart Propertiesの活用により観光・宿泊業界、ヘルスケア業界、自治体の業務効率化と生産性の向上をサポート' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      {/* ═══════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════ */}
      <div
        ref={heroRef}
        className="relative w-full flex items-center justify-center overflow-hidden"
        style={{ height: '100vh' }}
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            transform: `translateY(${scrollY * 0.2}px) scale(1.05)`,
            willChange: 'transform',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, rgba(10,25,47,0.7) 0%, rgba(10,25,47,0.85) 50%, rgba(10,25,47,0.95) 100%)',
          }}
        />

        {/* Grid Overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(201,162,39,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(201,162,39,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />

        {/* Scan Line */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(201,162,39,0.2) 30%, rgba(201,162,39,0.45) 50%, rgba(201,162,39,0.2) 70%, transparent 100%)',
              animation: 'heroScanLine 8s ease-in-out infinite',
            }}
          />
        </div>

        {/* Data Streams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {heroStreams.map((line) => (
            <div
              key={line.id}
              style={{
                position: 'absolute',
                left: `${line.x}%`,
                top: 0,
                width: '1px',
                height: `${line.height}%`,
                background: `linear-gradient(180deg, transparent 0%, rgba(201,162,39,${line.opacity * 3}) 40%, rgba(201,162,39,${line.opacity}) 100%)`,
                animation: `heroDataStream ${line.duration}s ease-in-out ${line.delay}s infinite`,
              }}
            />
          ))}
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {heroParticles.map((p) => (
            <div
              key={p.id}
              style={{
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                borderRadius: '50%',
                background: '#C9A227',
                opacity: p.opacity,
                animation: `heroFloat ${p.duration}s ease-in-out ${p.delay}s infinite`,
                '--drift-x': `${p.driftX}px`,
                '--drift-y': `${p.driftY}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute"
            style={{
              width: '600px',
              height: '600px',
              background: 'radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 65%)',
              top: '20%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              filter: 'blur(80px)',
              animation: 'heroPulseGlow 9s ease-in-out infinite',
            }}
          />
        </div>

        {/* Content */}
        <div
          className="relative z-10 text-center px-6 max-w-4xl mx-auto"
          style={{
            transform: `translateY(${heroParallax}px)`,
            opacity: heroOpacity,
            willChange: 'transform, opacity',
          }}
        >
          {/* Tag */}
          <div
            className="flex items-center justify-center gap-4 mb-8"
            style={{
              opacity: heroMounted ? 1 : 0,
              transform: heroMounted ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
            }}
          >
            <div
              className="w-12 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, #C9A227)' }}
            />
            <p
              className="text-xs tracking-widest-plus uppercase"
              style={{
                color: '#C9A227',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
              }}
            >
              Generative AI & Voice AI Solutions
            </p>
            <div
              className="w-12 h-px"
              style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }}
            />
          </div>

          {/* Main Title */}
          <h1
            className="text-4xl md:text-5xl lg:text-7xl leading-tight mb-8"
            style={{
              fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
              fontWeight: 500,
              color: '#FFFFFF',
              letterSpacing: '0.06em',
              opacity: heroMounted ? 1 : 0,
              transform: heroMounted ? 'translateY(0)' : 'translateY(28px)',
              transition: 'opacity 0.9s ease 0.4s, transform 0.9s ease 0.4s',
            }}
          >
            {t('home.heroTitle')}
            <br />
            <span style={{ color: '#C9A227' }}>{t('home.heroTitleHighlight')}</span>
          </h1>

          {/* Subtitle */}
          <p
            className="text-base md:text-lg leading-relaxed mb-12 max-w-2xl mx-auto"
            style={{
              color: '#FFFFFF',
              fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
              opacity: heroMounted ? 1 : 0,
              transform: heroMounted ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.9s ease 0.6s, transform 0.9s ease 0.6s',
            }}
          >
            {t('home.heroSubtitle')}
          </p>

          {/* CTA Buttons */}
          <div
            className="flex items-center justify-center gap-5 flex-wrap"
            style={{
              opacity: heroMounted ? 1 : 0,
              transform: heroMounted ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s',
            }}
          >
            <Link
              to="/business"
              className="whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                padding: '14px 36px',
                background: '#C9A227',
                color: '#0A192F',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#D4AF37';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              {t('home.heroCta1')}
            </Link>
            <Link
              to="/contact"
              className="whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                padding: '14px 36px',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#FFFFFF',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#C9A227';
                (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.4)';
                (e.currentTarget as HTMLAnchorElement).style.color = '#FFFFFF';
              }}
            >
              {t('home.heroCta2')}
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
          style={{ opacity: heroOpacity }}
        >
          <span
            className="text-xs tracking-widest-plus uppercase"
            style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Inter', sans-serif" }}
          >
            Scroll
          </span>
          <div className="relative" style={{ width: '1px', height: '40px', background: 'rgba(255,255,255,0.15)' }}>
            <div
              className="absolute top-0 left-0 w-full"
              style={{
                height: '50%',
                background: 'linear-gradient(180deg, #C9A227, transparent)',
                animation: 'gentlePulse 2.5s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MISSION SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={missionRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10"
        style={{ background: '#FAF9F6' }}
      >
        <div className="max-w-[900px] mx-auto text-center">
          <div
            style={{
              opacity: missionVisible ? 1 : 0,
              transform: missionVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.9s ease, transform 0.9s ease',
            }}
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A227)' }} />
              <i className="ri-shining-line text-lg" style={{ color: '#C9A227' }} />
              <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }} />
            </div>
            <h2
              className="text-xl md:text-2xl lg:text-3xl leading-relaxed mb-6"
              style={{
                fontFamily: i18n.language === 'ja'
                  ? "'Noto Serif JP', 'Yu Mincho', 'YuMincho', 'Hiragino Mincho ProN', serif"
                  : i18n.language === 'zh-TW'
                    ? "'Noto Serif TC', 'Source Han Serif TC', serif"
                    : "'Noto Serif JP', 'Playfair Display', serif",
                fontWeight: 500,
                color: '#1A1A1A',
                letterSpacing: '0.04em',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              }}
            >
              {t('home.missionText')}
            </h2>
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, transparent, #D4CFC4)' }} />
              <div className="w-1.5 h-1.5" style={{ background: '#C9A227', transform: 'rotate(45deg)' }} />
              <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, #D4CFC4, transparent)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SERVICES SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={servicesRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10"
        style={{ background: '#FFFFFF' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <ElegantSectionTitle
              tag={t('home.servicesTag')}
              title={t('home.servicesTitle')}
              subtitle={t('home.servicesSubtitle')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {services.map((svc, i) => (
              <ServiceCard
                key={i}
                image={svc.image}
                icon={svc.icon}
                title={svc.title}
                description={svc.description}
                index={i}
                visible={servicesVisible}
                learnMore={t('home.learnMore')}
              />
            ))}
          </div>
          <div
            className="mt-14 flex justify-center"
            style={{
              opacity: servicesVisible ? 1 : 0,
              transform: servicesVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s',
            }}
          >
            <Link
              to="/business"
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                padding: '14px 40px',
                border: '1px solid #C9A227',
                color: '#C9A227',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                (e.currentTarget as HTMLAnchorElement).style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
              }}
            >
              {t('home.servicesViewAll')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          TECHNOLOGY SKILLS SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={techRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10"
        style={{ background: '#FAF9F6' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <ElegantSectionTitle
              tag={t('home.techTag')}
              title={t('home.techTitle')}
              subtitle={t('home.techSubtitle')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {techSkills.map((skill, i) => (
              <div
                key={i}
                className="group p-6 border transition-all duration-500"
                style={{
                  borderColor: 'rgba(212, 207, 196, 0.5)',
                  background: '#FFFFFF',
                  opacity: techVisible ? 1 : 0,
                  transform: techVisible ? 'translateY(0)' : 'translateY(28px)',
                  transition: `opacity 0.7s ease ${0.06 + i * 0.07}s, transform 0.7s ease ${0.06 + i * 0.07}s, border-color 0.3s`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.4)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.5)';
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center shrink-0"
                    style={{ border: '1px solid rgba(201, 162, 39, 0.3)' }}
                  >
                    <i className={`${skill.icon} text-base`} style={{ color: '#C9A227' }} />
                  </div>
                  <div>
                    <h3
                      className="text-base font-medium mb-1.5"
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        color: '#1A1A1A',
                        fontWeight: 500,
                        fontSize: '1rem',
                      }}
                    >
                      {skill.title}
                    </h3>
                    <p
                      className="text-xs leading-relaxed"
                      style={{
                        color: '#333333',
                        fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                      }}
                    >
                      {skill.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          ABOUT PREVIEW SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={aboutRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10"
        style={{ background: '#FAF9F6' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div
              style={{
                opacity: aboutVisible ? 1 : 0,
                transform: aboutVisible ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'opacity 0.9s ease, transform 0.9s ease',
              }}
            >
              <div className="relative">
                <img
                  src={ABOUT_IMG}
                  alt="TradFit Office"
                  loading="lazy"
                  decoding="async"
                  className="w-full object-cover"
                  style={{ aspectRatio: '4/3' }}
                />
                {/* Gold frame accents */}
                <div
                  className="absolute top-4 left-4 w-16 h-16"
                  style={{
                    borderTop: '1px solid rgba(201,162,39,0.5)',
                    borderLeft: '1px solid rgba(201,162,39,0.5)',
                  }}
                />
                <div
                  className="absolute bottom-4 right-4 w-16 h-16"
                  style={{
                    borderBottom: '1px solid rgba(201,162,39,0.5)',
                    borderRight: '1px solid rgba(201,162,39,0.5)',
                  }}
                />
              </div>
            </div>

            {/* Text */}
            <div
              style={{
                opacity: aboutVisible ? 1 : 0,
                transform: aboutVisible ? 'translateX(0)' : 'translateX(30px)',
                transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s',
              }}
            >
              <ElegantSectionTitle
                tag={t('home.aboutTag')}
                title={t('home.aboutTitle')}
                subtitle={t('home.aboutSubtitle')}
              />
              <div className="mt-8 space-y-4">
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#333333',
                    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                  }}
                >
                  {t('home.aboutText1')}
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#333333',
                    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                  }}
                >
                  {t('home.aboutText2')}
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
                  style={{
                    fontFamily: "'Noto Serif JP', serif",
                    fontWeight: 500,
                    padding: '12px 28px',
                    border: '1px solid #C9A227',
                    color: '#C9A227',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                    (e.currentTarget as HTMLAnchorElement).style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                    (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
                  }}
                >
                  {t('home.aboutBtn')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          NEWS PREVIEW SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={newsRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10"
        style={{ background: '#FFFFFF' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <ElegantSectionTitle
              tag={t('home.newsTag')}
              title={t('home.newsTitle')}
            />
            <Link
              to="/news"
              className="shrink-0 inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                padding: '10px 24px',
                border: '1px solid #C9A227',
                color: '#C9A227',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                (e.currentTarget as HTMLAnchorElement).style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = '#C9A227';
              }}
            >
              {t('home.newsViewAll')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div>
            {newsItems.map((item, i) => (
              <Link
                key={i}
                to="/news"
                className="no-underline block"
              >
                <NewsPreviewItem
                  date={item.date}
                  category={item.category}
                  title={item.title}
                  index={i}
                  visible={newsVisible}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          CTA SECTION
      ═══════════════════════════════════════ */}
      <section
        ref={ctaRef as React.RefObject<HTMLElement>}
        className="py-24 md:py-32 px-6 md:px-10 relative overflow-hidden"
        style={{ background: '#0A192F' }}
      >
        {/* Subtle ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute"
            style={{
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 65%)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              filter: 'blur(60px)',
            }}
          />
        </div>

        <div className="max-w-[800px] mx-auto text-center relative z-10">
          <div
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.9s ease, transform 0.9s ease',
            }}
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A227)' }} />
              <i className="ri-mail-line text-lg" style={{ color: '#C9A227' }} />
              <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }} />
            </div>
            <h2
              className="text-2xl md:text-3xl lg:text-4xl leading-snug mb-6 text-center"
              style={{
                fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
                fontWeight: 500,
                color: '#FFFFFF',
                letterSpacing: '0.04em',
                whiteSpace: 'pre-line',
              }}
            >
              {t('home.ctaTitle')}
            </h2>
            <p
              className="text-base md:text-lg leading-relaxed mb-10 max-w-lg mx-auto"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
              }}
            >
              {t('home.ctaSubtitle')}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
                fontWeight: 500,
                padding: '14px 40px',
                background: '#C9A227',
                color: '#0A192F',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#D4AF37';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              {t('home.ctaBtn')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <ElegantFooter />

      <style>{`
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        @keyframes heroScanLine {
          0%   { top: -2px; opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 0.5; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes heroDataStream {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 1; }
          80%  { opacity: 0.5; }
          100% { transform: translateY(400%); opacity: 0; }
        }
        @keyframes heroFloat {
          0%   { transform: translate(0, 0); }
          33%  { transform: translate(calc(var(--drift-x) * 0.5), calc(var(--drift-y) * 0.5)); }
          66%  { transform: translate(var(--drift-x), var(--drift-y)); }
          100% { transform: translate(0, 0); }
        }
        @keyframes heroPulseGlow {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%, -50%) scale(1.15); }
        }
      `}</style>
    </div>
  );
}